<template>
    <TransitionRoot appear as="template" :show="show">
        <Dialog as="div" class="fixed inset-0 overflow-hidden" @close="close()">
            <div class="absolute inset-0 overflow-hidden">
                <DialogOverlay class="absolute inset-0 bg-gray-500 opacity-75" />

                <div class="fixed inset-y-0 pl-16 max-w-full right-0 flex">
                    <TransitionChild as="template" enter="transform transition ease-in-out duration-500 sm:duration-700"
                        enter-from="translate-x-full" enter-to="translate-x-0"
                        leave="transform transition ease-in-out duration-500 sm:duration-700" leave-from="translate-x-0"
                        leave-to="translate-x-full">
                        <div class="w-screen" ref="createService">
                            <form class="h-full divide-y divide-gray-200 flex flex-col bg-white shadow-xl"
                                @submit.prevent="submit">
                                <div class="flex-1 h-0 overflow-y-auto">
                                    <div class="py-6 px-4 bg-dark-pitch-purple sm:px-6">
                                        <div class="flex items-center justify-between">
                                            <DialogTitle class="text-lg text-white" v-if="form.id">
                                                Update Service `{{ form.name }}`
                                            </DialogTitle>
                                            <DialogTitle class="text-lg text-white" v-else>
                                                Create Service
                                            </DialogTitle>
                                            <div class="ml-3 h-7 flex items-center">
                                                <button type="button"
                                                    class="bg-dark-pitch-purple rounded-md text-indigo-200 hover:text-white focus:outline-none focus:ring-2 focus:ring-white"
                                                    @click="close()">
                                                    <span class="sr-only">Close panel</span>
                                                    <XIcon class="h-6 w-6" aria-hidden="true" />
                                                </button>
                                            </div>
                                        </div>
                                        <div class="mt-1">
                                            <p class="text-sm text-indigo-300">
                                                Get started by filling in the information below to create a new
                                                service.
                                            </p>
                                        </div>
                                    </div>
                                    <SlideOutErrors :show="showErrors === true" />
                                    <div class="flex-1 flex flex-col justify-between">
                                        <div class="px-4 divide-y divide-gray-200 sm:px-6">
                                            <div class="pt-6 pb-5 grid grid-cols-12 gap-3 items-center">
                                                <h2 class="col-span-12 text-lg text-bold">Service Information</h2>
                                                <div class="col-span-6 grid grid-cols-3 gap-3 row-span-3">
                                                    <div class="col-span-1">
                                                        <label for="service_name"
                                                            class="block text-sm text-gray-900 required">
                                                            Item
                                                        </label>
                                                        <div class="mt-1">
                                                            <input type="text" id="service_name" v-model="form.name"
                                                                class="block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md"
                                                                :class="{ 'border-red-500': form.errors.name }" />
                                                        </div>
                                                    </div>

                                                    <div class="col-span-1">
                                                        <label for="service_starts_at"
                                                            class="block text-sm text-gray-900">
                                                            Start Date <span v-if="formattedStartDate">({{
                                                                formattedStartDate }})</span>
                                                        </label>
                                                        <div class="mt-1">
                                                            <input type="date" id="service_starts_at"
                                                                v-model="form.starts_at"
                                                                class="block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md"
                                                                :class="{ 'border-red-500': form.errors.starts_at }" />
                                                        </div>
                                                    </div>

                                                    <div class="col-span-1">
                                                        <label for="service_starts_at"
                                                            class="block text-sm text-gray-900">
                                                            End Date <span v-if="formattedEndDate">({{ formattedEndDate
                                                                }})</span>
                                                        </label>
                                                        <div class="mt-1">
                                                            <input type="date" id="service_ends_at"
                                                                v-model="form.ends_at"
                                                                class="block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md"
                                                                :class="{ 'border-red-500': form.errors.ends_at }" />
                                                        </div>
                                                    </div>

                                                    <div class="col-span-3">
                                                        <span class="block text-sm text-gray-900 flex items-center">
                                                            <span class="required">Supplier</span>
                                                            <a class="text-xs text-light-pitch-purple mx-3 flex flex-auto"
                                                                target="_blank"
                                                                :href="route('frontend.organisations.create', ['supplier'])">
                                                                <svg class="-ml-0.5 mr-2 h-4 w-4"
                                                                    xmlns="http://www.w3.org/2000/svg" fill="none"
                                                                    viewBox="0 0 24 24" stroke="currentColor"
                                                                    aria-hidden="true">
                                                                    <path stroke-linecap="round" stroke-linejoin="round"
                                                                        stroke-width="2"
                                                                        d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z">
                                                                    </path>
                                                                </svg>
                                                                Create New Supplier
                                                            </a>
                                                            <a class="text-xs text-light-pitch-purple mx-3 cursor-pointer flex flex-auto"
                                                                @click="refreshSuppliers()">
                                                                <RefreshIcon
                                                                    class="w-4 h-4 text-light-pitch-purple cursor-pointer mr-2">
                                                                </RefreshIcon>
                                                                Refresh Suppliers
                                                            </a>
                                                            <a class="text-xs text-light-pitch-purple mx-3 flex flex-auto"
                                                                href="mailto:sports.payments@destinationsport.com?subject=New Supplier Request&body=Please can you add a new supplier to the Pitch System, the details are as follows:"
                                                                target="_blank">
                                                                <MailIcon class="w-4 h-4 text-light-pitch-purple mr-2">
                                                                </MailIcon>
                                                                New Supplier Request
                                                            </a>
                                                        </span>
                                                        <div class="relative mt-1">
                                                            <v-select class="h-full mt-1"
                                                                :class="{ 'border-red-500': form.errors.organisation_id }"
                                                                v-model="form.organisation_id" label="name"
                                                                :options="suppliers"
                                                                :reduce="suppliers => suppliers.id" />
                                                        </div>
                                                    </div>

                                                    <div class="col-span-1">
                                                        <label for="folder-name"
                                                            class="block text-sm text-gray-900 required flex items-center">
                                                            Country
                                                            <Popper :hover="true" class="cursor-pointer" placement="top"
                                                                content="Once the country is selected, only suppliers based in that location will be displayed.">
                                                                <InformationCircleIcon class="h-5 w-5 text-gray-400"
                                                                    aria-hidden="true" />
                                                            </Popper>
                                                        </label>
                                                        <v-select class="h-full mt-1"
                                                            :class="{ 'border-red-500': form.errors.country_id }"
                                                            :options="countries" label="name"
                                                            :reduce="countries => countries.id"
                                                            v-model="form.country_id" />
                                                    </div>

                                                    <div class="col-span-1">
                                                        <label for="folder-name" class="block text-sm text-gray-900">
                                                            Region
                                                        </label>
                                                        <v-select :disabled="regions?.length === 0" class="h-full mt-1"
                                                            :class="{ 'border-red-500': form.errors.region_id }"
                                                            :options="regions" label="name"
                                                            :reduce="regions => regions.id" v-model="form.region_id" />
                                                    </div>

                                                    <div class="col-span-1">
                                                        <label for="folder-name"
                                                            class="block text-sm text-gray-900 required">
                                                            Service Type
                                                        </label>
                                                        <v-select class="h-full mt-1"
                                                            :class="{ 'border-red-500': form.errors.service_type_id }"
                                                            :options="service_types" label="name"
                                                            :reduce="service_types => service_types.id"
                                                            v-model="form.service_type_id" />
                                                    </div>

                                                </div>
                                                <div class="col-span-6 grid grid-cols-1 row-span-3">
                                                    <div>
                                                        <label
                                                            class="block text-sm font-medium text-gray-700">Description</label>
                                                        <div class="mt-1">
                                                            <quill-editor content-type="html"
                                                                v-model:content="form.description"
                                                                :class="{ 'border-red-500': form.errors.description }"
                                                                class="min-h-[150px] mt-1 block w-full bg-white border border-gray-300 shadow-sm py-2 px-3"
                                                                theme="snow"></quill-editor>
                                                        </div>
                                                    </div>

                                                </div>

                                                <h2 class="col-span-12 text-lg text-bold">Buy Information</h2>

                                                <div class="tax_rates col-span-12 grid grid-cols-12 gap-3 row-span-6" v-if="fixture.trading_details.multiple_tax_rates == 1">
                                                    <div class="col-span-2">
                                                        <label for="buy_price" class="block text-sm text-gray-900 required">
                                                            Price
                                                        </label>
                                                        <div class="relative mt-1">
                                                            <div class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                                                                <span class="text-gray-500 sm:text-sm">
                                                                    {{ quote.buy_currency.symbol }}
                                                                </span>
                                                            </div>
                                                            <input type="text" id="buy_price" v-model="form.buy_price" @input="filterPriceInput('buy_price', $event)"
                                                                class="pl-10 block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md"
                                                                :class="{ 'border-red-500': form.errors.buy_price }" />
                                                        </div>
                                                    </div>

                                                    <div class="col-span-2">
                                                        <label for="buy_unit"
                                                            class="block text-sm text-gray-900 required flex items-center">
                                                            <span>Qty. <span
                                                                    class="text-xs text-gray-800 font-normal">e.g.
                                                                    (room/seat/people)</span></span>
                                                            <Popper :hover="true" class="cursor-pointer" placement="top"
                                                                content="The quantity purchased per item.">
                                                                <InformationCircleIcon class="h-5 w-5 text-gray-400"
                                                                    aria-hidden="true" />
                                                            </Popper>
                                                        </label>
                                                        <div class="mt-1">
                                                            <input type="number" id="buy_unit" v-model="form.buy_unit"
                                                                class="block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md"
                                                                :class="{ 'border-red-500': form.errors.buy_unit }" />
                                                        </div>
                                                    </div>

                                                    <div class="col-span-2">
                                                        <label for="buy_no"
                                                            class="block text-sm text-gray-900 required flex items-center">
                                                            <span>No. <span
                                                                    class="text-xs text-gray-800 font-normal">e.g.
                                                                    (night/days/meals)</span></span>
                                                            <Popper :hover="true" class="cursor-pointer" placement="top"
                                                                content="The number of items required.">
                                                                <InformationCircleIcon class="h-5 w-5 text-gray-400"
                                                                    aria-hidden="true" />
                                                            </Popper>
                                                        </label>
                                                        <div class="mt-1">
                                                            <input type="number" id="buy_no" v-model="form.buy_no"
                                                                class="block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md"
                                                                :class="{ 'border-red-500': form.errors.buy_no }" />
                                                        </div>
                                                    </div>

                                                    <div class="col-span-3">
                                                        <label :for="buy_net_total" class="block text-sm text-gray-900">
                                                            Net Total
                                                        </label>
                                                        <div class="relative mt-1">
                                                            <div
                                                                class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                                                                <span class="text-gray-500 sm:text-sm">{{
                                                                    quote.buy_currency.symbol }}</span>
                                                            </div>
                                                            <input disabled type="text" :id="buy_net_total"
                                                                v-model="form.buy_net_total"
                                                                class="pl-10 bg-gray-100 cursor-not-allowed block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md" />
                                                        </div>
                                                    </div>

                                                    <div class="col-span-3">
                                                        <label :for="buy_gross_total"
                                                            class="block text-sm text-gray-900">
                                                            Gross Total
                                                        </label>
                                                        <div class="relative mt-1">
                                                            <div
                                                                class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                                                                <span class="text-gray-500 sm:text-sm">{{
                                                                    quote.buy_currency.symbol }}</span>
                                                            </div>
                                                            <input disabled type="text" :id="buy_gross_total"
                                                                v-model="form.buy_gross_total"
                                                                class="pl-10 bg-gray-100 cursor-not-allowed block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md" />
                                                        </div>
                                                    </div>

                                                    <div class="tax_rate_row col-span-12 grid grid-cols-12 gap-3 row-span-6"
                                                        v-for="(tax_line, index) in form.tax_lines" :key="index">

                                                        <div v-if="tax_line.type == 'flat'" class="col-span-2">
                                                            <label :for="'flat_amount' + index"
                                                                class="block text-sm text-gray-900 required">
                                                                Amount
                                                            </label>
                                                            <div class="relative mt-1">
                                                                <div
                                                                    class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                                                                    <span class="text-gray-500 sm:text-sm">{{ quote.buy_currency.symbol }}</span>
                                                                </div>
                                                                <input type="number" :id="'flat_amount' + index"
                                                                    v-model="tax_line.flat_amount"
                                                                    class="pl-10 block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md"
                                                                    min="0.01" step="0.01" />
                                                            </div>
                                                        </div>
                                                        <div v-else class="col-span-2">
                                                            <label :for="'tax_rate' + index"
                                                                class="block text-sm text-gray-900 required">
                                                                Tax Rate
                                                            </label>
                                                            <div class="relative mt-1">
                                                                <input type="number" :id="'tax_rate' + index"
                                                                    v-model="tax_line.tax_rate"
                                                                    class="pr-10 block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md"
                                                                    min="0.01" max="100" step="0.01" />
                                                                <div
                                                                    class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                                                                    <span class="text-gray-500 sm:text-sm">%</span>
                                                                </div>
                                                            </div>
                                                        </div>

                                                        <div class="col-span-2">
                                                            <label :for="'tax_rate' + index"
                                                                class="block text-sm text-gray-900 required">
                                                                Tax Label
                                                            </label>
                                                            <div class="relative mt-1">
                                                                <input type="text" :id="'tax_rate' + index"
                                                                    v-model="tax_line.tax_label"
                                                                    class="pr-10 block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md" />
                                                            </div>
                                                        </div>

                                                        <div class="col-span-2">
                                                            <label :for="'costing_type' + index"
                                                                class="block text-sm text-gray-900 required">
                                                                Costing Type
                                                            </label>
                                                            <select :id="'costing_type' + index"
                                                                v-model="tax_line.costing_type"
                                                                class="mt-1 block w-full bg-white border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm">
                                                                <option v-for="(costing_type, index) in costing_types"
                                                                    :key="index" :value="costing_type"
                                                                    v-html="costing_type"></option>
                                                            </select>
                                                        </div>

                                                        <div class="col-span-3">
                                                            <label :for="'add_to_gross' + index"
                                                                class="block text-sm text-gray-900 required">
                                                                Add to gross
                                                            </label>
                                                            <select :id="'add_to_gross' + index"
                                                                v-model="tax_line.add_to_gross"
                                                                class="mt-1 block w-full bg-white border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm">
                                                                <option :value="1">Yes</option>
                                                                <option :value="0">No</option>
                                                            </select>
                                                        </div>

                                                        <div class="col-span-2">
                                                            <label :for="'tax_amount' + index"
                                                                class="block text-sm text-gray-900">
                                                                Tax Amount
                                                            </label>
                                                            <div class="relative mt-1">
                                                                <div
                                                                    class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                                                                    <span class="text-gray-500 sm:text-sm">{{
                                                                        quote.buy_currency.symbol }}</span>
                                                                </div>
                                                                <input v-if="tax_line.flat_amount" disabled type="text" :id="'tax_amount' + index"
                                                                    v-model="tax_line.flat_amount"
                                                                    class="pl-10 bg-gray-100 cursor-not-allowed block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md" />
                                                                <input v-else disabled type="text" :id="'tax_amount' + index"
                                                                    v-model="tax_line.tax_amount"
                                                                    class="pl-10 bg-gray-100 cursor-not-allowed block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md" />
                                                            </div>
                                                        </div>

                                                        <div class="col-span-1">
                                                            <button type="button" @click="removeTax(index)"
                                                                class="mt-6 py-2 px-4 border border-transparent shadow-sm text-sm rounded-md text-white bg-red-500 hover:bg-dark-red-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                                                                <span>Delete</span>
                                                            </button>
                                                        </div>
                                                    </div>
                                                    <div class="col-span-12 flex items-center">
                                                        <div class="flex space-x-2">
                                                            <button type="button" @click="addTax"
                                                                class="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm rounded-md text-white bg-pitch-purple hover:bg-dark-pitch-purple focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                                                                <span>Add Percentage</span>
                                                            </button>
                                                            <button type="button" @click="addFlatTax"
                                                                class="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm rounded-md text-white bg-pitch-purple hover:bg-dark-pitch-purple focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                                                                <span>Add Flat Amount</span>
                                                            </button>
                                                        </div>
                                                        <button type="button" @click="copyBuyToSell"
                                                            class="ml-auto inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm rounded-md text-white bg-pitch-purple hover:bg-dark-pitch-purple focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                                                            <span>Copy Buy to Sell</span>
                                                        </button>
                                                    </div>
                                                </div>
                                                <div class="tax_rates col-span-12 grid grid-cols-12 gap-3 row-span-6"
                                                    v-if="fixture.trading_details.multiple_tax_rates != 1">
                                                    <div class="col-span-2">
                                                        <label for="buy_price"
                                                            class="block text-sm text-gray-900 required">
                                                            Price
                                                        </label>
                                                        <div class="relative mt-1">
                                                            <div
                                                                class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                                                                <span class="text-gray-500 sm:text-sm">{{
                                                                    quote.buy_currency.symbol }}</span>
                                                            </div>
                                                            <input type="text" id="buy_price" v-model="form.buy_price" @input="filterPriceInput('buy_price', $event)"
                                                                class="pl-10 block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md"
                                                                :class="{ 'border-red-500': form.errors.buy_price }" />
                                                        </div>
                                                    </div>

                                                    <div class="col-span-1">
                                                        <label for="buy_costing_type"
                                                            class="block text-sm text-gray-900 required">
                                                            Costing Type
                                                        </label>
                                                        <select id="buy_costing_type" v-model="form.buy_costing_type"
                                                            :class="{ 'border-red-500': form.errors.buy_costing_type }"
                                                            class="mt-1 block w-full bg-white border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm">
                                                            <option value selected v-html="'Select'"></option>
                                                            <option v-for="(costing_type, index) in costing_types"
                                                                :key="index" :value="costing_type"
                                                                v-html="costing_type"></option>
                                                        </select>
                                                    </div>

                                                    <div class="col-span-2">
                                                        <label for="buy_vat_rate"
                                                            class="block text-sm text-gray-900 required">
                                                            VAT Percentage
                                                        </label>
                                                        <div class="mt-1">
                                                            <select id="buy_vat_rate" v-model="form.buy_vat_rate_id"
                                                                :class="{ 'border-red-500': form.errors.buy_vat_rate_id }"
                                                                class="mt-1 block w-full bg-white border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm">
                                                                <option value selected v-html="'Select'"></option>
                                                                <option v-for="vat_rate in vat_rates"
                                                                    :value="vat_rate.id" :key="vat_rate.id">
                                                                    {{ vat_rate.label }}
                                                                </option>

                                                            </select>
                                                        </div>
                                                    </div>

                                                    <div class="col-span-2">
                                                        <label for="buy_unit"
                                                            class="block text-sm text-gray-900 required flex items-center">
                                                            <span>Qty. <span
                                                                    class="text-xs text-gray-800 font-normal">e.g.
                                                                    (room/seat/people)</span></span>
                                                            <Popper :hover="true" class="cursor-pointer" placement="top"
                                                                content="The quantity purchased per item.">
                                                                <InformationCircleIcon class="h-5 w-5 text-gray-400"
                                                                    aria-hidden="true" />
                                                            </Popper>
                                                        </label>
                                                        <div class="mt-1">
                                                            <input type="number" id="buy_unit" v-model="form.buy_unit"
                                                                class="block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md"
                                                                :class="{ 'border-red-500': form.errors.buy_unit }" />
                                                        </div>
                                                    </div>

                                                    <div class="col-span-2">
                                                        <label for="buy_no"
                                                            class="block text-sm text-gray-900 required flex items-center">
                                                            <span>No. <span
                                                                    class="text-xs text-gray-800 font-normal">e.g.
                                                                    (night/days/meals)</span></span>
                                                            <Popper :hover="true" class="cursor-pointer" placement="top"
                                                                content="The number of items required.">
                                                                <InformationCircleIcon class="h-5 w-5 text-gray-400"
                                                                    aria-hidden="true" />
                                                            </Popper>
                                                        </label>
                                                        <div class="mt-1">
                                                            <input type="number" id="buy_no" v-model="form.buy_no"
                                                                class="block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md"
                                                                :class="{ 'border-red-500': form.errors.buy_no }" />
                                                        </div>
                                                    </div>

                                                    <div class="col-span-1">
                                                        <label for="bill_back"
                                                            class="block text-sm text-gray-900 required">
                                                            Bill Back
                                                        </label>
                                                        <div class="mt-1">
                                                            <select id="buy_vat_rate" v-model="form.bill_back"
                                                                :class="{ 'border-red-500': form.errors.bill_back }"
                                                                class="mt-1 block w-full bg-white border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm">
                                                                <option value="0">No</option>
                                                                <option value="1">Yes</option>
                                                            </select>
                                                        </div>
                                                    </div>

                                                    <div class="col-span-1">
                                                        <label for="buy_net_total" class="block text-sm text-gray-900">
                                                            Net Total
                                                        </label>
                                                        <div class="relative mt-1">
                                                            <div
                                                                class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                                                                <span class="text-gray-500 sm:text-sm">{{
                                                                    quote.buy_currency.symbol }}</span>
                                                            </div>
                                                            <input disabled type="text" id="buy_net_total"
                                                                v-model="form.buy_net_total"
                                                                class="pl-10 bg-gray-100 cursor-not-allowed block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md"
                                                                :class="{ 'border-red-500': form.errors.buy_net_total }" />
                                                        </div>
                                                    </div>

                                                    <div class="col-span-1">
                                                        <label for="buy_gross_total"
                                                            class="block text-sm text-gray-900">
                                                            Gross Total
                                                        </label>
                                                        <div class="relative mt-1">
                                                            <div
                                                                class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                                                                <span class="text-gray-500 sm:text-sm">{{
                                                                    quote.buy_currency.symbol }}</span>
                                                            </div>
                                                            <input disabled type="text" id="buy_gross_total"
                                                                v-model="form.buy_gross_total"
                                                                class="pl-10 bg-gray-100 cursor-not-allowed block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md"
                                                                :class="{ 'border-red-500': form.errors.buy_gross_total }" />
                                                        </div>
                                                    </div>
                                                </div>

                                                <h2 class="col-span-12 text-lg text-bold">Sell Information
                                                    <span class="text-xs text-light-pitch-purple cursor-pointer"
                                                        @click="toggleSellInformation()"
                                                        v-if="fixture.trading_details.multiple_tax_rates != 1">
                                                        <span v-if="showSellInputs === false">Display</span>
                                                        <span v-else>Hide</span>
                                                        all sell information
                                                    </span>
                                                    <span class="text-xs"
                                                        v-if="fixture.trading_details.multiple_tax_rates == 1">
                                                        The buy information entered into the fields will be used for the sell information.
                                                        <span class="text-light-pitch-purple cursor-pointer"
                                                            @click="toggleSellInformation()">
                                                            <span v-if="showSellInputs === false">Click here to show details</span>
                                                            <span v-else>Click here to hide details</span>
                                                        </span>
                                                    </span>
                                                </h2>

                                                <template v-if="fixture.trading_details.multiple_tax_rates != 1">
                                                    <div class="col-span-2">
                                                        <label for="sell_price" class="block text-sm text-gray-900 required">
                                                            Price
                                                        </label>
                                                        <div class="relative mt-1">
                                                            <div class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                                                                <span class="text-gray-500 sm:text-sm">
                                                                    {{ quote.sell_currency.symbol }}
                                                                </span>
                                                            </div>
                                                            <input type="text" id="sell_price" v-model="form.sell_price" @input="filterPriceInput('sell_price', $event)"
                                                                class="pl-10 block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md"
                                                                :class="{ 'border-red-500': form.errors.sell_price }" />
                                                        </div>
                                                    </div>

                                                    <div class="col-span-1"
                                                        :class="{ 'hidden': showSellInputs === false }">
                                                        <label for="sell_costing_type"
                                                            class="block text-sm text-gray-900 required">
                                                            Costing Type
                                                        </label>
                                                        <div class="mt-1">
                                                            <select id="sell_costing_type"
                                                                v-model="form.sell_costing_type"
                                                                :class="{ 'border-red-500': form.errors.sell_costing_type }"
                                                                class="mt-1 block w-full bg-white border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm">
                                                                <option value selected v-html="'Select'"></option>
                                                                <option v-for="(costing_type, index) in costing_types"
                                                                    :value="costing_type" v-html="costing_type">
                                                                </option>
                                                            </select>
                                                        </div>
                                                    </div>

                                                    <div class="col-span-2"
                                                        :class="{ 'hidden': showSellInputs === false }">
                                                        <label for="sell_vat_rate"
                                                            class="block text-sm text-gray-900 required">
                                                            VAT Percentage
                                                        </label>
                                                        <div class="mt-1">
                                                            <select id="sell_vat_rate" v-model="form.sell_vat_rate_id"
                                                                :class="{ 'border-red-500': form.errors.sell_vat_rate_id }"
                                                                class="mt-1 block w-full bg-white border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm">
                                                                <option value selected v-html="'Select'"></option>
                                                                <option v-for="vat_rate in vat_rates"
                                                                    :value="vat_rate.id">
                                                                    {{ vat_rate.label }}
                                                                </option>
                                                            </select>
                                                        </div>
                                                    </div>

                                                    <div class="col-span-2"
                                                        :class="{ 'hidden': showSellInputs === false }">
                                                        <label for="sell_unit"
                                                            class="block text-sm text-gray-900 required flex items-center">
                                                            <span>Qty. <span
                                                                    class="text-xs text-gray-800 font-normal">e.g.
                                                                    (room/seat/people)</span></span>
                                                            <Popper :hover="true" class="cursor-pointer" placement="top"
                                                                content="The quantity purchased per item.">
                                                                <InformationCircleIcon class="h-5 w-5 text-gray-400"
                                                                    aria-hidden="true" />
                                                            </Popper>
                                                        </label>
                                                        <div class="mt-1">
                                                            <input type="number" id="sell_unit" v-model="form.sell_unit"
                                                                class="block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md"
                                                                :class="{ 'border-red-500': form.errors.sell_unit }" />
                                                        </div>
                                                    </div>

                                                    <div class="col-span-2"
                                                        :class="{ 'hidden': showSellInputs === false }">
                                                        <label for="sell_no"
                                                            class="block text-sm text-gray-900 required flex items-center">
                                                            <span>No. <span
                                                                    class="text-xs text-gray-800 font-normal">e.g.
                                                                    (night/days/meals)</span></span>
                                                            <Popper :hover="true" class="cursor-pointer" placement="top"
                                                                content="The number of items required.">
                                                                <InformationCircleIcon class="h-5 w-5 text-gray-400"
                                                                    aria-hidden="true" />
                                                            </Popper>
                                                        </label>
                                                        <div class="mt-1">
                                                            <input type="number" id="sell_no" v-model="form.sell_no"
                                                                class="block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md"
                                                                :class="{ 'border-red-500': form.errors.sell_no }" />
                                                        </div>
                                                    </div>

                                                    <div v-if="showSellInputs === false" class="col-span-8"></div>
                                                    <div v-else class="col-span-1"></div>

                                                    <div class="col-span-1">
                                                        <label for="sell_net_total" class="block text-sm text-gray-900">
                                                            Net Total
                                                        </label>
                                                        <div class="relative mt-1">
                                                            <div
                                                                class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                                                                <span class="text-gray-500 sm:text-sm">{{
                                                                    quote.sell_currency.symbol }}</span>
                                                            </div>
                                                            <input disabled type="text" id="sell_net_total"
                                                                v-model="form.sell_net_total"
                                                                class="pl-10 bg-gray-100 cursor-not-allowed block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md"
                                                                :class="{ 'border-red-500': form.errors.sell_net_total }" />
                                                        </div>
                                                    </div>

                                                    <div class="col-span-1">
                                                        <label for="sell_gross_total"
                                                            class="block text-sm text-gray-900">
                                                            Gross Total
                                                        </label>
                                                        <div class="relative mt-1">
                                                            <div
                                                                class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                                                                <span class="text-gray-500 sm:text-sm">{{
                                                                    quote.sell_currency.symbol }}</span>
                                                            </div>
                                                            <input disabled type="text" id="sell_gross_total"
                                                                v-model="form.sell_gross_total"
                                                                class="pl-10 bg-gray-100 cursor-not-allowed block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md"
                                                                :class="{ 'border-red-500': form.errors.sell_gross_total }" />

                                                        </div>
                                                    </div>
                                                </template>

                                                <div v-if="fixture.trading_details.multiple_tax_rates == 1" class="col-span-12 grid grid-cols-12 gap-3 row-span-6" :class="{ 'hidden': showSellInputs === false }">
                                                    <div class="col-span-2">
                                                        <label for="sell_price" class="block text-sm text-gray-900 required">
                                                            Price
                                                        </label>
                                                        <div class="relative mt-1">
                                                            <div class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                                                                <span class="text-gray-500 sm:text-sm">
                                                                    {{ quote.sell_currency.symbol }}
                                                                </span>
                                                            </div>
                                                            <input type="text" id="sell_price" v-model="form.sell_price" @input="filterPriceInput('sell_price', $event)"
                                                                class="pl-10 block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md"
                                                                :class="{ 'border-red-500': form.errors.sell_price }" />
                                                        </div>
                                                    </div>

                                                    <div class="col-span-2">
                                                        <label for="sell_unit"
                                                            class="block text-sm text-gray-900 required flex items-center">
                                                            <span>Qty. <span
                                                                    class="text-xs text-gray-800 font-normal">e.g.
                                                                    (room/seat/people)</span></span>
                                                            <Popper :hover="true" class="cursor-pointer" placement="top"
                                                                content="The quantity purchased per item.">
                                                                <InformationCircleIcon class="h-5 w-5 text-gray-400"
                                                                    aria-hidden="true" />
                                                            </Popper>
                                                        </label>
                                                        <div class="mt-1">
                                                            <input type="number" id="sell_unit" v-model="form.sell_unit"
                                                                class="block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md"
                                                                :class="{ 'border-red-500': form.errors.sell_unit }" />
                                                        </div>
                                                    </div>

                                                    <div class="col-span-2">
                                                        <label for="sell_no"
                                                            class="block text-sm text-gray-900 required flex items-center">
                                                            <span>No. <span
                                                                    class="text-xs text-gray-800 font-normal">e.g.
                                                                    (night/days/meals)</span></span>
                                                            <Popper :hover="true" class="cursor-pointer" placement="top"
                                                                content="The number of items required.">
                                                                <InformationCircleIcon class="h-5 w-5 text-gray-400"
                                                                    aria-hidden="true" />
                                                            </Popper>
                                                        </label>
                                                        <div class="mt-1">
                                                            <input type="number" id="sell_no" v-model="form.sell_no"
                                                                class="block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md"
                                                                :class="{ 'border-red-500': form.errors.sell_no }" />
                                                        </div>
                                                    </div>

                                                    <div class="col-span-3">
                                                        <label for="sell_net_total" class="block text-sm text-gray-900">
                                                            Net Total
                                                        </label>
                                                        <div class="relative mt-1">
                                                            <div
                                                                class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                                                                <span class="text-gray-500 sm:text-sm">{{
                                                                    quote.sell_currency.symbol }}</span>
                                                            </div>
                                                            <input disabled type="text" id="sell_net_total"
                                                                v-model="form.sell_net_total"
                                                                class="pl-10 bg-gray-100 cursor-not-allowed block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md"
                                                                :class="{ 'border-red-500': form.errors.sell_net_total }" />
                                                        </div>
                                                    </div>

                                                    <div class="col-span-3">
                                                        <label for="sell_gross_total"
                                                            class="block text-sm text-gray-900">
                                                            Gross Total
                                                        </label>
                                                        <div class="relative mt-1">
                                                            <div
                                                                class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                                                                <span class="text-gray-500 sm:text-sm">{{
                                                                    quote.sell_currency.symbol }}</span>
                                                            </div>
                                                            <input disabled type="text" id="sell_gross_total"
                                                                v-model="form.sell_gross_total"
                                                                class="pl-10 bg-gray-100 cursor-not-allowed block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md"
                                                                :class="{ 'border-red-500': form.errors.sell_gross_total }" />

                                                        </div>
                                                    </div>

                                                    <div class="sell_tax_rate_row col-span-12 grid grid-cols-12 gap-3 row-span-6"
                                                        v-for="(tax_line, index) in form.sell_tax_lines" :key="index">
                                                        <div class="col-span-2">
                                                            <div v-if="tax_line.type == 'flat'" class="col-span-2">
                                                                <label :for="'sell_flat_amount' + index"
                                                                    class="block text-sm text-gray-900 required">
                                                                    Amount
                                                                </label>
                                                                <div class="relative mt-1">
                                                                    <div
                                                                        class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                                                                        <span class="text-gray-500 sm:text-sm">{{ quote.buy_currency.symbol }}</span>
                                                                    </div>
                                                                    <input type="number" :id="'sell_flat_amount' + index"
                                                                        v-model="tax_line.flat_amount"
                                                                        class="pl-10 block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md"
                                                                        min="0" step="0.01" />
                                                                </div>
                                                            </div>
                                                            <div v-else class="col-span-2">
                                                                <label :for="'sell_tax_rate' + index"
                                                                    class="block text-sm text-gray-900 required">
                                                                    Tax Rate
                                                                </label>
                                                                <div class="relative mt-1">
                                                                    <input type="number" :id="'sell_tax_rate' + index"
                                                                        v-model="tax_line.tax_rate"
                                                                        class="pr-10 block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md"
                                                                        min="0" max="100" step="0.01" />
                                                                    <div
                                                                        class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                                                                        <span class="text-gray-500 sm:text-sm">%</span>
                                                                    </div>
                                                                </div>
                                                            </div>

                                                        </div>

                                                        <div class="col-span-2">
                                                            <label :for="'sell_tax_rate' + index"
                                                                class="block text-sm text-gray-900 required">
                                                                Tax Label
                                                            </label>
                                                            <div class="relative mt-1">
                                                                <input type="text" :id="'sell_tax_rate' + index"
                                                                    v-model="tax_line.tax_label"
                                                                    class="pr-10 block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md" />
                                                            </div>
                                                        </div>

                                                        <div class="col-span-2">
                                                            <label :for="'sell_costing_type' + index"
                                                                class="block text-sm text-gray-900 required">
                                                                Costing Type
                                                            </label>
                                                            <select :id="'sell_costing_type' + index"
                                                                v-model="tax_line.costing_type"
                                                                class="mt-1 block w-full bg-white border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm">
                                                                <option v-for="(costing_type, index) in costing_types"
                                                                    :key="index" :value="costing_type"
                                                                    v-html="costing_type"></option>
                                                            </select>
                                                        </div>

                                                        <div class="col-span-3">
                                                            <label :for="'sell_add_to_gross' + index"
                                                                class="block text-sm text-gray-900 required">
                                                                Add to gross
                                                            </label>
                                                            <select :id="'sell_add_to_gross' + index"
                                                                v-model="tax_line.add_to_gross"
                                                                class="mt-1 block w-full bg-white border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm">
                                                                <option :value="1">Yes</option>
                                                                <option :value="0">No</option>
                                                            </select>
                                                        </div>

                                                        <div class="col-span-2">
                                                            <label :for="'sell_tax_amount' + index"
                                                                class="block text-sm text-gray-900">
                                                                Tax Amount
                                                            </label>
                                                            <div class="relative mt-1">
                                                                <div
                                                                    class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                                                                    <span class="text-gray-500 sm:text-sm">{{
                                                                        quote.buy_currency.symbol }}</span>
                                                                </div>
                                                                <input disabled type="text"
                                                                    :id="'sell_tax_amount' + index"
                                                                    v-model="tax_line.tax_amount"
                                                                    class="pl-10 bg-gray-100 cursor-not-allowed block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md" />
                                                            </div>
                                                        </div>

                                                        <div class="col-span-1">
                                                            <button type="button" @click="removeSellTax(index)"
                                                                class="mt-6 py-2 px-4 border border-transparent shadow-sm text-sm rounded-md text-white bg-red-500 hover:bg-dark-red-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                                                                <span>Delete</span>
                                                            </button>
                                                        </div>
                                                    </div>
                                                    <div class="col-span-3">
                                                        <button type="button" @click="addSellTax"
                                                            class="mr-2 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm rounded-md text-white bg-pitch-purple hover:bg-dark-pitch-purple focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                                                            <span>Add Percentage</span>
                                                        </button>
                                                        <button type="button" @click="addFlatSellTax"
                                                            class="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm rounded-md text-white bg-pitch-purple hover:bg-dark-pitch-purple focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                                                            <span>Add Flat Amount</span>
                                                        </button>
                                                    </div>
                                                </div>

                                                <div class="col-span-12 flex items-center text-sm">
                                                    <h2 class="text-lg text-bold">Commission</h2>
                                                    <Popper :hover="true" class="cursor-pointer" placement="top"
                                                        v-if="form.commission_notes"
                                                        v-model:content="form.commission_notes">
                                                        <InformationCircleIcon class="h-5 w-5 text-gray-400"
                                                            aria-hidden="true" />
                                                    </Popper>
                                                </div>

                                                <div class="col-span-2">
                                                    <label for="commission_type" class="block text-sm text-gray-900">
                                                        Percentage or Fixed Amount
                                                    </label>
                                                    <div class="mt-1">
                                                        <select id="commission_type" v-model="form.commission_type"
                                                            :class="{ 'border-red-500': form.errors.commission_type }"
                                                            class="mt-1 block w-full bg-white border border-gray-300 rounded-md shadow-sm py-2 px-3  focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm">
                                                            <option value selected v-html="'None'"></option>
                                                            <option v-for="(amount_type, index) in amount_types"
                                                                v-html="amount_type" :value="amount_type"></option>
                                                        </select>
                                                    </div>
                                                </div>

                                                <div class="col-span-1">
                                                    <label for="folder-name" class="block text-sm text-gray-900">
                                                        Costing Type
                                                    </label>
                                                    <div class="mt-1">
                                                        <select id="commission_costing_type"
                                                            v-model="form.commission_costing_type"
                                                            :class="{ 'border-red-500': form.errors.commission_costing_type }"
                                                            class="mt-1 block w-full bg-white border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm"
                                                            :disabled="!form.commission_type">
                                                            <option value selected v-html="'Select'"></option>
                                                            <option v-for="(costing_type, index) in costing_types"
                                                                :value="costing_type" v-html="costing_type"></option>
                                                        </select>
                                                    </div>
                                                </div>

                                                <div class="col-span-2">
                                                    <label for="commission_amount" class="block text-sm text-gray-900">
                                                        Amount
                                                    </label>
                                                    <div class="relative mt-1">
                                                        <div v-if="form.commission_type === 'Fixed Amount'" class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                                                            <span class="text-gray-500 sm:text-sm">
                                                                {{ quote.buy_currency.symbol }}
                                                            </span>
                                                        </div>
                                                        <input type="text" id="commission_amount" v-model="form.commission_amount" @input="filterPriceInput('commission_amount', $event)"
                                                            class=" block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md"
                                                            :class="{ 'border-red-500': form.errors.commission_amount, 'pl-7 pr-12': form.commission_type === 'Fixed Amount' }"
                                                            :disabled="!form.commission_costing_type" />
                                                    </div>
                                                </div>


                                                <div class="col-span-3">

                                                </div>

                                                <div class="col-span-2">
                                                    <label for="commission_total" class="block text-sm text-gray-900">
                                                        Total
                                                    </label>
                                                    <div class="relative mt-1">
                                                        <div
                                                            class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                                                            <span class="text-gray-500 sm:text-sm">{{
                                                                quote.buy_currency.symbol }}</span>
                                                        </div>
                                                        <input disabled type="text" id="commission_total"
                                                            v-model="form.commission_total"
                                                            class="pl-10 pr-12 bg-gray-100 cursor-not-allowed block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md"
                                                            :class="{ 'border-red-500': form.errors.commission_total }" />

                                                    </div>
                                                </div>


                                                <div class="col-span-1">
                                                </div>

                                                <h2 class="col-span-12 text-lg text-bold">Management Fee</h2>


                                                <div class="col-span-2">
                                                    <label for="management_fee_type"
                                                        class="block text-sm text-gray-900">
                                                        Percentage or Fixed Amount
                                                    </label>
                                                    <div class="mt-1">
                                                        <select id="country" v-model="form.management_fee_type"
                                                            :class="{ 'border-red-500': form.errors.management_fee_type }"
                                                            class="mt-1 block w-full bg-white border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm">
                                                            <option value selected v-html="'None'"></option>
                                                            <option v-for="(amount_type, index) in amount_types"
                                                                v-html="amount_type" :value="amount_type"></option>
                                                        </select>
                                                    </div>
                                                </div>

                                                <div class="col-span-1">
                                                    <label for="folder-name" class="block text-sm text-gray-900">
                                                        Costing Type
                                                    </label>
                                                    <div class="mt-1">
                                                        <select id="country" v-model="form.management_fee_costing_type"
                                                            :class="{ 'border-red-500': form.errors.management_fee_costing_type }"
                                                            class="mt-1 block w-full bg-white border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm"
                                                            :disabled="!form.management_fee_type">
                                                            <option value selected v-html="'Select'"></option>
                                                            <option v-for="(costing_type, index) in costing_types"
                                                                :value="costing_type" v-html="costing_type"></option>
                                                        </select>
                                                    </div>
                                                </div>

                                                <div class="col-span-2">
                                                    <label for="folder-name" class="block text-sm text-gray-900">
                                                        Amount
                                                    </label>
                                                    <div class="relative mt-1">
                                                        <div v-if="form.management_fee_type === 'Fixed Amount'" class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                                                            <span class="text-gray-500 sm:text-sm">
                                                                {{ quote.sell_currency.symbol }}
                                                            </span>
                                                        </div>
                                                        <input type="text" id="folder-name" v-model="form.management_fee_amount" @input="filterPriceInput('management_fee_amount', $event)"
                                                            class="block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md"
                                                            :class="{ 'border-red-500': form.errors.management_fee_amount, 'pl-7 pr-12': form.management_fee_type === 'Fixed Amount' }"
                                                            :disabled="!form.management_fee_costing_type" />
                                                    </div>
                                                </div>

                                                <div class="col-span-2">
                                                    <label for="management_fee_add_vat"
                                                        class="block text-sm text-gray-900">
                                                        Add VAT
                                                    </label>
                                                    <div class="mt-1">
                                                        <select id="management_fee_add_vat" v-model="form.management_fee_includes_vat"
                                                            :class="{ 'border-red-500': form.errors.management_fee_includes_vat }"
                                                            class="mt-1 block w-full bg-white border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm"
                                                            :disabled="!form.management_fee_type">
                                                            <option :value="0">No</option>
                                                            <option :value="1">Yes</option>
                                                        </select>
                                                    </div>
                                                </div>

                                                <div class="col-span-1">

                                                </div>

                                                <div class="col-span-2">
                                                    <label for="management_fee_tax_amount" class="block text-sm text-gray-900">
                                                        Tax Amount
                                                    </label>
                                                    <div class="relative mt-1">
                                                        <div
                                                            class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                                                            <span class="text-gray-500 sm:text-sm">{{
                                                                quote.sell_currency.symbol }}</span>
                                                        </div>
                                                        <input disabled type="text" id="management_fee_tax_amount"
                                                            v-model="form.management_fee_tax_amount"
                                                            class="pl-10 pr-12 bg-gray-100 cursor-not-allowed block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md"
                                                            :class="{ 'border-red-500': form.errors.management_fee_tax_amount }" />

                                                    </div>
                                                </div>

                                                <div class="col-span-1">
                                                    <label for="management_fee_net_total" class="block text-sm text-gray-900">
                                                        Net Total
                                                    </label>
                                                    <div class="relative mt-1">
                                                        <div
                                                            class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                                                            <span class="text-gray-500 sm:text-sm">{{
                                                                quote.sell_currency.symbol }}</span>
                                                        </div>
                                                        <input disabled type="text" id="management_fee_net_total"
                                                            v-model="form.management_fee_net_total"
                                                            class="pl-10 pr-12 bg-gray-100 cursor-not-allowed block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md" />

                                                    </div>
                                                </div>

                                                <div class="col-span-1">
                                                    <label for="management_fee_gross_total" class="block text-sm text-gray-900">
                                                        Gross Total
                                                    </label>
                                                    <div class="relative mt-1">
                                                        <div
                                                            class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                                                            <span class="text-gray-500 sm:text-sm">{{
                                                                quote.sell_currency.symbol }}</span>
                                                        </div>
                                                        <input disabled type="text" id="management_fee_gross_total"
                                                            v-model="form.management_fee_gross_total"
                                                            class="pl-10 pr-12 bg-gray-100 cursor-not-allowed block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md" />

                                                    </div>
                                                </div>


                                                <div class="col-span-1">
                                                </div>
                                            </div>

                                        </div>
                                    </div>

                                </div>
                                <div class="flex-shrink-0 px-4 py-4 flex justify-end">
                                    <button type="button"
                                        class="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                        @click="close()">
                                        Close
                                    </button>
                                    <button :disabled="form.processing || !form.isDirty" type="submit"
                                        class=" ml-4 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm rounded-md text-white bg-pitch-purple hover:bg-dark-pitch-purple focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-gray-200">
                                        <span v-if="updating === true">Update</span>
                                        <span v-else>Save</span>
                                    </button>
                                </div>
                            </form>
                        </div>
                    </TransitionChild>
                </div>
            </div>
        </Dialog>
    </TransitionRoot>
</template>
<script>
import { ref, watch } from 'vue'
import {
    Combobox,
    ComboboxButton,
    ComboboxInput,
    ComboboxLabel,
    ComboboxOption,
    ComboboxOptions,
    Dialog,
    DialogOverlay,
    DialogTitle,
    TransitionChild,
    TransitionRoot
} from '@headlessui/vue'
import { XIcon } from '@heroicons/vue/outline'
import {
    CheckIcon,
    ExclamationCircleIcon,
    LinkIcon,
    PlusSmIcon,
    QuestionMarkCircleIcon,
    SelectorIcon,
    RefreshIcon,
    InformationCircleIcon,
    MailIcon
} from '@heroicons/vue/solid'
import { useForm, Link, usePage } from '@inertiajs/vue3'
import SlideOutErrors from "@/Components/SlideOutErrors";
import { mapState } from "vuex";
import { emitter } from "@/eventHub";
import Popper from "vue3-popper";
import moment from "moment";

export default {
    components: {
        SlideOutErrors,
        Dialog,
        DialogOverlay,
        DialogTitle,
        TransitionChild,
        TransitionRoot,
        LinkIcon,
        PlusSmIcon,
        QuestionMarkCircleIcon,
        ExclamationCircleIcon,
        XIcon,
        Combobox,
        ComboboxButton,
        ComboboxInput,
        ComboboxLabel,
        ComboboxOption,
        ComboboxOptions,
        CheckIcon,
        SelectorIcon,
        Link,
        RefreshIcon,
        Popper,
        InformationCircleIcon,
        MailIcon
    },
    props: {
        quote: Object,
        fixture: Object
    },
    data() {
        return {
            show: ref(false),
            showErrors: ref(false),
            updating: ref(false),
            query: '',
            service: [],
            vat_rates: this.fixture.country.vat_rates ?? [],
            showSellInputs: this.fixture.trading_details.multiple_tax_rates == 1 ? true : false,
            suppliers: usePage().props.suppliers ?? [],
            clients: usePage().props.clients ?? [],
            countries: usePage().props.countries ?? [],
            regions: usePage().props.regions ?? [],
            service_types: usePage().props.service_types ?? [],
            form: useForm({
                id: null,
                quote_id: null,
                folder_id: null,
                name: null,
                starts_at: null,
                ends_at: null,
                country_id: "",
                region_id: "",
                service_type_id: "",
                organisation_id: "",
                description: "",
                buy_price: 0.00,
                buy_costing_type: "",
                buy_vat_rate_id: "",
                buy_unit: 0,
                buy_no: 0,
                buy_gross_total: this.buy_gross_total ?? 0,
                buy_net_total: this.buy_net_total ?? 0,
                sell_price: 0.00,
                sell_costing_type: "",
                sell_vat_rate_id: "",
                sell_unit: 0,
                sell_no: 0,
                sell_net_total: 0.00,
                sell_gross_total: 0.00,
                commission_type: "",
                commission_costing_type: "",
                commission_amount: 0.00,
                commission_notes: null,
                commission_includes_vat: 0,
                commission_total: 0.00,
                management_fee_type: "",
                management_fee_costing_type: "",
                management_fee_vat_rate_id: "",
                management_fee_tax_percentage: 0.00,
                management_fee_amount: 0.00,
                management_fee_includes_vat: 0,
                management_fee_total: 0.00,
                management_fee_tax_amount: 0.00,
                management_fee_net_total: 0.00,
                management_fee_gross_total: 0.00,
                bill_back: 0,
                tax_lines: [{
                    type: '',
                    tax_rate: 0.00,
                    tax_label: '',
                    costing_type: '',
                    add_to_gross: '',
                    tax_amount: '',
                    flat_amount: 0.00
                }],
                sell_tax_lines: [{
                    type: '',
                    tax_rate: 0.00,
                    tax_label: '',
                    costing_type: '',
                    add_to_gross: '',
                    tax_amount: '',
                    flat_amount: 0.00
                }],
            })
        }
    },
    computed: {
        ...mapState({
            currencies: state => state.services.currencies ?? null,
            costing_types: state => state.services.costing_types ?? null,
            amount_types: state => state.services.amount_types ?? null,
        }),
        filteredSuppliers() {
            let region_id = this.form.region_id;
            let service_type_id = this.form.service_type_id;
            let organisation_id = this.form.organisation_id;

            // return this.suppliers;

            const selectedSupplier = this.suppliers.find(supplier => supplier.id == organisation_id);

            // Create a filtered list of suppliers
            return this.suppliers.filter((supplier) => {


                // Ensure the supplier has a commission rate set
                if (selectedSupplier?.service?.commission_rate_percent != null) {
                    // Setting the amount to supplier level
                    if ((this.form.commission_amount == '' && !this.form.commission_costing_type) || (!this.form.commission_costing_type)) {
                        this.form.commission_amount = selectedSupplier?.service.commission_rate_percent;
                    }
                }

                // Return true for a supplier if it matches the specified criteria
                return supplier.id == organisation_id || (
                    // Check if the country_id in the form matches the supplier's address country_id (or always true if form's country_id is empty)
                    (this.form.country_id !== "" ? supplier.service?.address.country_id === this.form.country_id : true) &&
                    // Check if the region_id in the form matches any of the supplier's address regions' id (or always true if form's region_id is empty)
                    (this.form.region_id !== "" ? supplier.service?.address.regions.some(function (o) { return o["id"] === region_id; }) : true) &&
                    // Check if the service_type_id in the form matches the supplier's service_type_id (or always true if form's service_type_id is empty)
                    (this.form.service_type_id !== "" ? supplier.service?.service_type_id === this.form.service_type_id : true)
                );
            });
        },
        serviceData() {
            const sell_price = this.form.sell_price;
            const sell_no = this.form.sell_no;
            const sell_unit = this.form.sell_unit;
            const sell_vat_rate_id = this.form.sell_vat_rate_id;
            const sell_costing_type = this.form.sell_costing_type;
            const buy_price = this.form.buy_price;
            const buy_no = this.form.buy_no;
            const buy_unit = this.form.buy_unit;
            const buy_vat_rate_id = this.form.buy_vat_rate_id;
            const buy_costing_type = this.form.buy_costing_type;
            const commission_type = this.form.commission_type;
            const commission_costing_type = this.form.commission_costing_type;
            const commission_includes_vat = this.form.commission_includes_vat;
            const commission_amount = this.form.commission_amount;
            const commission_notes = this.form.commission_notes;
            const management_fee_type = this.form.management_fee_type;
            const management_fee_costing_type = this.form.management_fee_costing_type;
            const management_fee_vat_rate_id = this.form.management_fee_vat_rate_id;
            const management_fee_includes_vat = this.form.management_fee_includes_vat;
            const management_fee_amount = this.form.management_fee_amount;
            const tax_lines = this.form.tax_lines ?? [{
                type: '',
                tax_rate: 0.00,
                tax_label: '',
                costing_type: '',
                add_to_gross: '',
                tax_amount: '',
                flat_amount: 0.00
            }];
            const sell_tax_lines = this.form.sell_tax_lines ?? [{
                type: '',
                tax_rate: 0.00,
                tax_label: '',
                costing_type: '',
                add_to_gross: '',
                tax_amount: '',
                flat_amount: 0.00
            }];

            return {
                sell_price,
                sell_no,
                sell_unit,
                sell_vat_rate_id,
                sell_costing_type,
                buy_price,
                buy_no,
                buy_unit,
                buy_vat_rate_id,
                buy_costing_type,
                commission_type,
                commission_costing_type,
                commission_includes_vat,
                commission_amount,
                commission_notes,
                management_fee_type,
                management_fee_costing_type,
                management_fee_vat_rate_id,
                management_fee_includes_vat,
                management_fee_amount,
                tax_lines,
                sell_tax_lines
            }
        },
        formattedStartDate() {
            if (this.form.starts_at) {
                return moment(this.form.starts_at).format('DD MMM YYYY');
            }
            return '';
        },
        formattedEndDate() {
            if (this.form.ends_at) {
                return moment(this.form.ends_at).format('DD MMM YYYY');
            }
            return '';
        },
    },
    methods: {
        addTax() {
            this.form.tax_lines.push({
                type: 'percentage',
                tax_rate: 0.00,
                costing_type: '',
                add_to_gross: '',
                net_total: '',
                gross_total: ''
            });
        },
        addFlatTax() {
            this.form.tax_lines.push({
                type: 'flat',
                tax_rate: 0.00,
                costing_type: '',
                add_to_gross: '',
                net_total: '',
                gross_total: '',
                flat_amount: 0.00
            });
        },
        addSellTax() {
            this.form.sell_tax_lines.push({
                type: 'percentage',
                tax_rate: 0.00,
                costing_type: '',
                add_to_gross: '',
                net_total: '',
                gross_total: ''
            });
        },
        addFlatSellTax() {
            this.form.sell_tax_lines.push({
                type: 'flat',
                tax_rate: 0.00,
                costing_type: '',
                add_to_gross: '',
                net_total: '',
                gross_total: '',
                flat_amount: 0.00
            });
        },
        copyBuyToSell() {
            this.form.sell_price = this.form.buy_price;
            this.form.sell_costing_type = this.form.buy_costing_type;
            this.form.sell_vat_rate_id = this.form.buy_vat_rate_id;
            this.form.sell_unit = this.form.buy_unit;
            this.form.sell_no = this.form.buy_no;

            this.form.sell_tax_lines = this.form.tax_lines.map(tax => ({
                ...tax
            }));

            this.showSellInputs = true;
        },
        removeTax(index) {
            this.form.tax_lines.splice(index, 1);
        },
        removeSellTax(index) {
            this.form.sell_tax_lines.splice(index, 1);
        },
        refreshSuppliers() {
            this.$store.dispatch('services/getAll', { 'fixture_id': this.fixture.id, 'quote_id': this.quote.id });
        },
        toggleSellInformation() {
            this.showSellInputs = !this.showSellInputs;
        },
        submit() {
            this.sanitizeFormMonetaryFields();

            if (this.updating === true) {
                this.form.post(route('frontend.fixture.quote.service.update', [this.fixture.id, this.quote.id,
                this.form.id]), {
                    onSuccess: page => {
                        this.show = false;
                        this.showErrors = false;
                        this.form.reset();
                    },
                    onError: errors => {
                        this.showErrors = true;
                    },
                    preserveScroll: true
                });
            } else {
                this.form.post(route('frontend.fixture.quote.service.create', [this.fixture.id, this.quote.id]), {
                    onSuccess: page => {
                        this.show = false;
                        this.showErrors = false;
                        this.form.reset();
                    },
                    onError: errors => {
                        this.showErrors = true;
                    },
                    preserveScroll: true
                });
            }
        },
        close() {
            this.form.reset();
            this.form.clearErrors();
            this.showErrors = false;
            this.show = false;
            this.showSellInputs = true;
        },
        open() {
            this.form.clearErrors();
            this.show = true;
        },
        number_format(nStr) {
            nStr += '';
            let x = nStr.split('.');
            let x1 = x[0];
            let x2 = x.length > 1 ? '.' + x[1] : '';
            let rgx = /(\d+)(\d{3})/;
            while (rgx.test(x1)) {
                x1 = x1.replace(rgx, '$1' + ',' + '$2');
            }
            return x1 + x2;
        },
        doRounding(value, decimals) {
            return Number(Math.round(value + "e" + decimals) + "e-" + decimals).toFixed(2);
        },
        sanitizePriceValue(value) {
            if (value === null || value === undefined || value === '') {
                return value;
            }

            const string = String(value).trim();
            const isNegative = string.startsWith('-');
            let sanitized = string.replace(/[^0-9.]/g, '');

            if (sanitized === '') {
                return isNegative ? '-' : sanitized;
            }

            const parts = sanitized.split('.');
            if (parts.length > 2) {
                sanitized = parts[0] + '.' + parts.slice(1).join('');
            }

            const normalizedParts = sanitized.split('.');
            if (normalizedParts.length === 2) {
                sanitized = normalizedParts[0] + '.' + normalizedParts[1].slice(0, 2);
            }

            return isNegative ? '-' + sanitized : sanitized;
        },
        sanitizeFormMonetaryFields() {
            const fields = [
                'buy_price',
                'buy_gross_total',
                'buy_net_total',
                'sell_price',
                'sell_gross_total',
                'sell_net_total',
                'commission_amount',
                'commission_total',
                'management_fee_amount',
                'management_fee_total',
                'management_fee_tax_amount',
                'management_fee_tax_percentage',
            ];

            fields.forEach((field) => {
                if (this.form[field] !== undefined && this.form[field] !== null && this.form[field] !== '') {
                    this.form[field] = this.sanitizePriceValue(this.form[field]);
                }
            });
        },
        filterPriceInput(field, e) {
            const value = this.sanitizePriceValue(e.target.value);
            this.form[field] = value;
            e.target.value = value;
        }
    },
    watch: {
        show: function (value) {
            if (value === true) {
                this.$store.dispatch('services/getAll', {
                    fixture_id: this.fixture.id,
                    quote_id: this.quote.id
                });

                this.vat_rates = this.fixture.country.vat_rates ?? [];
            }
        },
        'form.country_id': {
            deep: false,
            handler(id) {
                if (this.updating) return;
                // Is the country the UK and service type is hotel? If so set bill back to true
                id == 1 && this.form.service_type_id == 5 ? this.form.bill_back = 1 : this.form.bill_back = 0;
            }
        },
        'form.buy_price': {
            deep: false,
            handler(id) {
                if ((this.updating === false && this.showSellInputs === false)) {
                    this.form.sell_price = this.form.buy_price
                }
            }
        },
        'form.buy_costing_type': {
            deep: false,
            handler(id) {
                if (this.showSellInputs === false) {
                    this.form.sell_costing_type = this.form.buy_costing_type
                }
            }
        },
        'form.buy_unit': {
            deep: false,
            handler(id) {
                if (this.showSellInputs === false) {
                    this.form.sell_unit = this.form.buy_unit
                }
            }
        },
        'form.buy_vat_rate_id': {
            deep: false,
            handler(id) {
                if (this.showSellInputs === false) {
                    this.form.sell_vat_rate_id = this.form.buy_vat_rate_id
                }
            }
        },
        'form.buy_no': {
            deep: false,
            handler(id) {
                if (this.showSellInputs === false) {
                    this.form.sell_no = this.form.buy_no
                }
            }
        },
        'form.service_type_id': {
            deep: false,
            handler(id) {
                if (this.updating) return;
                // Is the country the UK and service type is hotel? If so set bill back to true
                id == 5 && this.form.country_id == 1 ? this.form.bill_back = 1 : this.form.bill_back = 0;
            }
        },
        selectedSupplier: {
            deep: true,
            handler(supplier) {
                if (supplier && supplier.id) {
                    this.form.organisation_id = supplier.id;
                }
            }
        },
        'form.organisation_id': {
            deep: false,
            handler(id) {
                const selectedSupplier = this.suppliers.find(supplier => supplier.id == this.form.organisation_id);

                // Ensure the supplier has commission notes set
                if (selectedSupplier?.service?.commission_notes != null) {
                    // Setting the commission notes
                    this.form.commission_notes = selectedSupplier?.service.commission_notes;
                } else {
                    this.form.commission_notes = null;
                }
            }
        },
        serviceData: {
            deep: true,
            handler(val) {
                let vat_rate;

                let buy_net_total = this.buy_net_total ?? parseFloat(val.buy_price) *
                    parseInt(val.buy_unit) * parseInt(val.buy_no);

                let buy_gross_total = this.buy_gross_total ?? parseFloat(val.buy_price) *
                    parseInt(val.buy_unit) * parseInt(val.buy_no);

                let sell_net_total = this.sell_net_total ?? parseFloat(val.sell_price) *
                    parseInt(val.sell_unit) * parseInt(val.sell_no);

                let sell_gross_total = this.sell_gross_total ?? parseFloat(val.sell_price) *
                    parseInt(val.sell_unit) * parseInt(val.sell_no);

                val.buy_price = this.sanitizePriceValue(val.buy_price);
                val.sell_price = this.sanitizePriceValue(val.sell_price);

                // Buy Calculations
                if (val.buy_vat_rate_id !== "" && val.buy_costing_type !== "" && val.buy_vat_rate_id !== null
                    && val.buy_costing_type !== null) {


                    if (val.buy_vat_rate_id !== "") {
                        vat_rate = this.vat_rates.find(vat_rate => vat_rate.id === val.buy_vat_rate_id);
                    }

                    if (vat_rate == null) {
                        vat_rate = {
                            percentage: 0
                        };
                    }
                    if (val.buy_costing_type === 'Gross') {
                        buy_gross_total = parseFloat(val.buy_price) *
                            parseInt(val.buy_unit) *
                            parseInt(val.buy_no);

                        buy_net_total = parseFloat(val.buy_price) / (1 +
                            (vat_rate.percentage / 100) ?? 0.1) *
                            parseInt(val.buy_unit) * parseInt(val.buy_no);

                        this.form.buy_gross_total = this.number_format(this.doRounding(buy_gross_total, 2));
                        this.form.buy_net_total = this.number_format(this.doRounding(buy_net_total, 2))
                    } else {

                        buy_gross_total = parseFloat(val.buy_price) * (1 + (vat_rate.percentage /
                            100)) *
                            parseInt(val.buy_unit) * parseInt(val.buy_no);
                        buy_net_total = parseFloat(val.buy_price) * parseInt(val.buy_unit) *
                            parseInt(val.buy_no);
                        this.form.buy_gross_total = this.number_format(this.doRounding(buy_gross_total, 2))
                        this.form.buy_net_total = this.number_format(this.doRounding(buy_net_total, 2));
                    }

                } else {
                    this.form.buy_gross_total = this.number_format(this.doRounding(buy_gross_total, 2));
                    this.form.buy_net_total = this.number_format(this.doRounding(buy_net_total, 2));
                }

                // Sell Calculations
                if (val.sell_vat_rate_id !== "" && val.sell_costing_type !== "" && val.sell_vat_rate_id
                    !== null
                    && val.sell_costing_type !== null) {


                    if (val.sell_vat_rate_id !== "") {
                        vat_rate = this.vat_rates.find(vat_rate => vat_rate.id === val.sell_vat_rate_id);
                    }

                    if (vat_rate == null) {
                        vat_rate = {
                            percentage: 0
                        };
                    }
                    if (val.sell_costing_type === 'Gross') {

                        let sell_gross_total = parseFloat(val.sell_price) * parseInt(val.sell_unit) *
                            parseInt(val.sell_no)

                        let sell_net_total = parseFloat(val.sell_price) / (1 +
                            (vat_rate.percentage / 100)) *
                            parseInt(val.sell_unit) * parseInt(val.sell_no);

                        this.form.sell_gross_total = this.number_format(this.doRounding(sell_gross_total, 2));
                        this.form.sell_net_total = this.number_format(this.doRounding(sell_net_total, 2))
                    } else {

                        let sell_gross_total = parseFloat(val.sell_price) * (1 + (vat_rate.percentage /
                            100)) *
                            parseInt(val.sell_unit) * parseInt(val.sell_no);

                        let sell_net_total = parseFloat(val.sell_price) * parseInt(val.sell_unit) *
                            parseInt(val.sell_no);

                        this.form.sell_gross_total = this.number_format(this.doRounding(sell_gross_total, 2))
                        this.form.sell_net_total = this.number_format(this.doRounding(sell_net_total, 2));
                    }

                } else {
                    this.form.sell_gross_total = this.number_format(this.doRounding(sell_gross_total, 2));
                    this.form.sell_net_total = this.number_format(this.doRounding(sell_net_total, 2));
                }

                // check to see tax_lines have at least one entry and if any of the entries have tax_rate, costing_type and add_to_gross set
                if (this.form.tax_lines.length > 0 && this.form.tax_lines.some(tl => (tl.tax_rate !== 0.00 && tl.costing_type !== "" && tl.add_to_gross !== "") || (tl.flat_amount && tl.flat_amount > 0))) {
                    let net_total = 0;
                    let gross_total = parseFloat(val.buy_price) * parseInt(val.buy_unit) * parseInt(val.buy_no);
                    let stackedgross = parseFloat(val.buy_price) * parseInt(val.buy_unit) * parseInt(val.buy_no);
                    let gross_has_been_stacked = false;

                    for (let [index, tax_line] of this.form.tax_lines.entries()) {
                        if ((tax_line.tax_rate !== 0.00 && tax_line.costing_type !== "" && tax_line.add_to_gross !== "") || (tax_line.flat_amount && tax_line.flat_amount > 0)) {
                            let tax_amount = 0;

                            if (tax_line.costing_type == 'Net') {

                                if (tax_line.add_to_gross == 1) {
                                    if (gross_has_been_stacked == true) {

                                        if(tax_line.flat_amount){
                                            tax_amount = parseFloat(tax_line.flat_amount);
                                        } else {
                                            tax_amount = stackedgross * (1 + (tax_line.tax_rate /
                                                100)) -
                                                stackedgross;
                                        }

                                        this.form.tax_lines[index].tax_amount = this.number_format(this.doRounding(tax_amount, 2));
                                    } else {
                                        if(tax_line.flat_amount){
                                            tax_amount = parseFloat(tax_line.flat_amount);
                                        } else {
                                            tax_amount = parseFloat(val.buy_price) * (1 + (tax_line.tax_rate /
                                                100)) *
                                                parseInt(val.buy_unit) * parseInt(val.buy_no) -
                                                (parseFloat(val.buy_price) * parseInt(val.buy_unit) * parseInt(val.buy_no));
                                        }

                                        this.form.tax_lines[index].tax_amount = this.number_format(this.doRounding(tax_amount, 2));
                                    }
                                    stackedgross += tax_amount;
                                    gross_has_been_stacked = true;
                                } else {
                                    if (gross_has_been_stacked == true) {
                                        if(tax_line.flat_amount){
                                            tax_amount = parseFloat(tax_line.flat_amount);
                                        } else {
                                            tax_amount = stackedgross * (1 + (tax_line.tax_rate /
                                                100)) -
                                                stackedgross;
                                        }

                                        this.form.tax_lines[index].tax_amount = this.number_format(this.doRounding(tax_amount, 2));
                                    } else {
                                        if(tax_line.flat_amount){
                                            tax_amount = parseFloat(tax_line.flat_amount);
                                        } else {
                                            tax_amount = parseFloat(val.buy_price) * (1 + (tax_line.tax_rate /
                                                100)) *
                                                parseInt(val.buy_unit) * parseInt(val.buy_no) -
                                                (parseFloat(val.buy_price) * parseInt(val.buy_unit) * parseInt(val.buy_no));
                                        }

                                        this.form.tax_lines[index].tax_amount = this.number_format(this.doRounding(tax_amount, 2));
                                    }

                                }

                                gross_total += parseFloat(tax_line.tax_amount.replace(/,/g, ''));
                            }

                            if (tax_line.costing_type == 'Gross') {
                                if(tax_line.flat_amount){
                                    tax_amount = parseFloat(tax_line.flat_amount);
                                } else {
                                    tax_amount = gross_total - parseFloat(val.buy_price) / (1 +
                                        (tax_line.tax_rate / 100) ?? 0.1) *
                                        parseInt(val.buy_unit) * parseInt(val.buy_no);
                                }

                                this.form.tax_lines[index].tax_amount = this.number_format(this.doRounding(tax_amount, 2));

                                net_total += parseFloat(tax_line.tax_amount.replace(/,/g, ''));
                            }
                        } else {
                            this.form.tax_lines[index].tax_amount = '';
                        }
                    }

                    if (gross_total > 0) {
                        this.form.buy_gross_total = this.number_format(this.doRounding(gross_total, 2));
                    } else {
                        this.form.buy_gross_total = this.number_format(this.doRounding(parseFloat(val.buy_price) * parseInt(val.buy_unit) * parseInt(val.buy_no), 2));
                    }

                    if (net_total > 0) {
                        this.form.buy_net_total = this.number_format(this.doRounding(gross_total - net_total, 2));
                    } else {
                        this.form.buy_net_total = this.number_format(this.doRounding(parseFloat(val.buy_price) * parseInt(val.buy_unit) * parseInt(val.buy_no), 2));
                    }
                } else {
                    this.form.tax_lines.forEach((tax_line, index) => {
                        this.form.tax_lines[index].tax_amount = '';
                    });
                }

                // check to see sell_tax_lines have at least one entry and if any of the entries have tax_rate, costing_type and add_to_gross set
                if (this.form.sell_tax_lines.length > 0 && this.form.sell_tax_lines.some(tl => (tl.tax_rate !== 0.00 && tl.costing_type !== "" && tl.add_to_gross !== "") || (tl.flat_amount && tl.flat_amount > 0))) {
                    let net_total = 0;
                    let gross_total = parseFloat(val.sell_price) * parseInt(val.sell_unit) * parseInt(val.sell_no);
                    let stackedgross = parseFloat(val.sell_price) * parseInt(val.sell_unit) * parseInt(val.sell_no);
                    let gross_has_been_stacked = false;

                    for (let [index, tax_line] of this.form.sell_tax_lines.entries()) {
                        if ((tax_line.tax_rate !== 0.00 && tax_line.costing_type !== "" && tax_line.add_to_gross !== "") || (tax_line.flat_amount && tax_line.flat_amount > 0)) {
                            let tax_amount = 0;

                            if (tax_line.costing_type == 'Net') {

                                if (tax_line.add_to_gross == 1) {
                                    if (gross_has_been_stacked == true) {
                                        if(tax_line.flat_amount){
                                            tax_amount = parseFloat(tax_line.flat_amount);
                                        } else {
                                            tax_amount = stackedgross * (1 + (tax_line.tax_rate /
                                                100)) -
                                                stackedgross;
                                        }

                                        this.form.sell_tax_lines[index].tax_amount = this.number_format(this.doRounding(tax_amount, 2));
                                    } else {
                                        if(tax_line.flat_amount){
                                            tax_amount = parseFloat(tax_line.flat_amount);
                                        } else {
                                            tax_amount = parseFloat(val.sell_price) * (1 + (tax_line.tax_rate /
                                                100)) *
                                                parseInt(val.sell_unit) * parseInt(val.sell_no) -
                                                (parseFloat(val.sell_price) * parseInt(val.sell_unit) * parseInt(val.sell_no));
                                        }

                                        this.form.sell_tax_lines[index].tax_amount = this.number_format(this.doRounding(tax_amount, 2));
                                    }
                                    stackedgross += tax_amount;
                                    gross_has_been_stacked = true;
                                } else {
                                    if (gross_has_been_stacked == true) {
                                        tax_amount = stackedgross * (1 + (tax_line.tax_rate /
                                            100)) -
                                            stackedgross;

                                        this.form.sell_tax_lines[index].tax_amount = this.number_format(this.doRounding(tax_amount, 2));
                                    } else {
                                        if(tax_line.flat_amount){
                                            tax_amount = parseFloat(tax_line.flat_amount);
                                        } else {
                                            tax_amount = parseFloat(val.sell_price) * (1 + (tax_line.tax_rate /
                                                100)) *
                                                parseInt(val.sell_unit) * parseInt(val.sell_no) -
                                                (parseFloat(val.sell_price) * parseInt(val.sell_unit) * parseInt(val.sell_no));
                                        }

                                        this.form.sell_tax_lines[index].tax_amount = this.number_format(this.doRounding(tax_amount, 2));
                                    }

                                }

                                gross_total += parseFloat(tax_line.tax_amount.replace(/,/g, ''));
                            }

                            if (tax_line.costing_type == 'Gross') {
                                if(tax_line.flat_amount){
                                    tax_amount = parseFloat(tax_line.flat_amount);
                                } else {
                                    tax_amount = gross_total - parseFloat(val.sell_price) / (1 +
                                        (tax_line.tax_rate / 100) ?? 0.1) *
                                        parseInt(val.sell_unit) * parseInt(val.sell_no);
                                }

                                this.form.sell_tax_lines[index].tax_amount = this.number_format(this.doRounding(tax_amount, 2));

                                net_total += parseFloat(tax_line.tax_amount.replace(/,/g, ''));
                            }
                        } else {
                            this.form.sell_tax_lines[index].tax_amount = '';
                        }
                    }

                    if (gross_total > 0) {
                        this.form.sell_gross_total = this.number_format(this.doRounding(gross_total, 2));
                    } else {
                        this.form.sell_gross_total = this.number_format(this.doRounding(parseFloat(val.sell_price) * parseInt(val.sell_unit) * parseInt(val.sell_no), 2));
                    }

                    if (net_total > 0) {
                        this.form.sell_net_total = this.number_format(this.doRounding(gross_total - net_total, 2));
                    } else {
                        this.form.sell_net_total = this.number_format(this.doRounding(parseFloat(val.sell_price) * parseInt(val.sell_unit) * parseInt(val.sell_no), 2));
                    }
                } else {
                    this.form.sell_tax_lines.forEach((tax_line, index) => {
                        this.form.sell_tax_lines[index].tax_amount = '';
                    });
                }

                // Reset all if commission type is none
                if (val.commission_type === '' || val.commission_type === null) {
                    this.form.commission_amount = 0;
                    this.form.commission_total = 0;
                }

                // Commission Calculations
                if (val.commission_amount !== null && val.commission_amount !== "") {
                    // Check the costing type
                    if (val.commission_costing_type === 'Gross') {
                        // Check the commission type
                        if (val.commission_type === 'Fixed Amount') {
                            this.form.commission_total = this.number_format(this.doRounding(val.commission_amount, 2));
                        } else {
                            this.form.commission_total =
                                this.number_format(this.doRounding(((parseFloat(this.form.buy_gross_total.replace(/,/g, '')) / 100) *
                                    parseFloat(val.commission_amount)), 2))
                        }
                    } else {
                        // Check the commission type
                        if (val.commission_type === 'Fixed Amount') {
                            this.form.commission_total =
                                this.number_format(this.doRounding(parseFloat(val.commission_amount), 2));
                        } else {
                            this.form.commission_total =
                                this.number_format(this.doRounding(((parseFloat(this.form.buy_net_total.replace(/,/g, '')) / 100) *
                                    parseFloat(val.commission_amount)), 2))
                        }
                    }
                }

                // Reset all if management fee type is none
                if (val.management_fee_type === '' || val.management_fee_type === null) {
                    this.form.management_fee_amount = 0;
                    this.form.management_fee_total = 0;
                    this.form.management_fee_tax_amount = 0;
                    this.form.management_fee_net_total = 0;
                    this.form.management_fee_gross_total = 0;
                    this.form.management_fee_tax_percentage = 0;
                    this.form.management_fee_includes_vat = 0;
                    this.form.management_fee_vat_rate_id = "";
                }

                // Management Fee Calculations
                if (val.management_fee_amount !== null && val.management_fee_amount !== "") {
                    // Check the costing type
                    if (val.management_fee_costing_type === 'Gross') {
                        // Check the management fee type
                        if (val.management_fee_type === 'Fixed Amount') {
                            this.form.management_fee_total =
                                this.number_format(this.doRounding(val.management_fee_amount, 2));
                        } else {
                            this.form.management_fee_total =
                                this.number_format(this.doRounding(((parseFloat(this.form.sell_gross_total.replace(/,/g, '')) / 100) *
                                    parseFloat(val.management_fee_amount)), 2))
                        }
                    } else {
                        // Check the management fee type
                        if (val.management_fee_type === 'Fixed Amount') {
                            this.form.management_fee_total =
                                this.number_format(this.doRounding(parseFloat(val.management_fee_amount), 2));
                        } else {
                            this.form.management_fee_total =
                                this.number_format(this.doRounding(((parseFloat(this.form.sell_net_total.replace(/,/g, '')) / 100) *
                                    parseFloat(val.management_fee_amount)), 2))
                        }
                    }

                    const addManagementFeeTax = parseInt(val.management_fee_includes_vat) === 1;
                    this.form.management_fee_vat_rate_id = addManagementFeeTax ? val.sell_vat_rate_id : "";

                    let managementFeeVatRate = addManagementFeeTax
                        ? this.vat_rates.find(vat_rate => vat_rate.id === val.sell_vat_rate_id)
                        : null;
                    let managementFeeTaxPercentage = addManagementFeeTax ? (managementFeeVatRate?.percentage ?? 0) : 0;
                    this.form.management_fee_tax_percentage = this.doRounding(managementFeeTaxPercentage, 2);

                    const managementFeeTotal = parseFloat(String(this.form.management_fee_total).replace(/,/g, '')) || 0;
                    let managementFeeTaxAmount = 0;
                    let managementFeeNetTotal = 0;
                    let managementFeeGrossTotal = 0;

                    // Management fee total is always treated as net.
                    managementFeeNetTotal = managementFeeTotal;
                    managementFeeTaxAmount = managementFeeNetTotal * (managementFeeTaxPercentage / 100);
                    managementFeeGrossTotal = managementFeeNetTotal + managementFeeTaxAmount;

                    this.form.management_fee_tax_amount = this.number_format(this.doRounding(managementFeeTaxAmount, 2));
                    this.form.management_fee_net_total = this.number_format(this.doRounding(managementFeeNetTotal, 2));
                    this.form.management_fee_gross_total = this.number_format(this.doRounding(managementFeeGrossTotal, 2));
                }
            }
        }
    },
    mounted() {
        // Create a new service, opens the create service slideout.
        emitter.on('createServiceSlideout', (folder_id) => {
            this.updating = false;
            this.form.quote_id = this.quote.id;
            this.form.folder_id = folder_id;
            this.form.country_id = this.fixture.country_id;
            this.form.bill_back = null;
            this.form.starts_at = this.fixture.starts_at;
            this.form.ends_at = this.fixture.starts_at;
            this.form.organisation_id = null;

            // Is billback false?
            if (!Boolean(Number(this.form.bill_back))) {
                // Is the service type is hotel and country the UK? If so set bill back to true
                this.form.service_type_id == 5 && this.form.country_id == 1 ? this.form.bill_back = 1 : this.form.bill_back = 0;
            }

            this.open();
        });

        // Edit an existing service, opens the create service slideout and populates the form.
        emitter.on("openServiceSlideout", (service) => {
            this.open();
            this.service = service;
            this.updating = true;
            this.form.id = service.id;
            this.form.quote_id = service.quote_id;
            this.form.folder_id = service.folder_id;
            this.form.name = service.name;
            this.form.description = service.description;
            this.form.organisation_id = service.organisation_id;
            this.form.buy_price = this.sanitizePriceValue(service.buy_price);
            this.form.buy_unit = parseFloat(service.buy_unit).toFixed(0);
            this.form.buy_no = parseFloat(service.buy_no).toFixed(0);
            this.form.buy_costing_type = service.buy_costing_type;
            this.form.buy_vat_rate_id = service.buy_vat_rate_id;
            this.form.sell_price = this.sanitizePriceValue(service.sell_price);
            this.form.sell_unit = service.sell_unit;
            this.form.sell_no = service.sell_no;
            this.form.sell_costing_type = service.sell_costing_type;
            this.form.sell_vat_rate_id = service.sell_vat_rate_id;
            this.form.commission_type = service.commission_type ?? "";
            this.form.commission_costing_type = service.commission_costing_type ?? "";
            this.form.commission_amount = service.commission_amount;
            this.form.commission_notes = service.commission_notes ?? "";
            this.form.management_fee_type = service.management_fee_type ?? "";
            this.form.management_fee_costing_type = service.management_fee_costing_type ?? "";
            this.form.management_fee_vat_rate_id = service.management_fee_vat_rate_id ?? "";
            this.form.management_fee_amount = service.management_fee_amount;
            this.form.management_fee_includes_vat = service.management_fee_includes_vat ?? 0;
            this.form.management_fee_tax_percentage = service.management_fee_tax_percentage ?? 0;
            this.form.management_fee_tax_amount = service.management_fee_tax_amount ?? 0;
            this.form.country_id = service.country_id;
            this.form.region_id = service.region_id;
            this.form.starts_at = service.starts_at;
            this.form.ends_at = service.ends_at;
            this.form.bill_back = service.bill_back
            this.form.service_type_id = service.service_type_id;
            this.form.tax_lines = service.tax_lines ?? [{
                type: '',
                tax_rate: 0.00,
                tax_label: '',
                costing_type: '',
                add_to_gross: '',
                tax_amount: '',
                flat_amount: 0.00,
            }];
            this.form.sell_tax_lines = service.sell_tax_lines ?? [{
                type: '',
                tax_rate: 0.00,
                tax_label: '',
                costing_type: '',
                add_to_gross: '',
                tax_amount: '',
                flat_amount: 0.00,
            }];
            if (this.fixture.trading_details.multiple_tax_rates == 1) {
                this.showSellInputs = true;
            } else if (
                this.form.buy_costing_type === this.form.sell_costing_type
                    && parseInt(this.form.buy_no) === parseInt(this.form.sell_no)
                    && parseInt(this.form.buy_unit) === parseInt(this.form.sell_unit)
                    && parseInt(this.form.buy_vat_rate_id) === parseInt(this.form.sell_vat_rate_id)) {
                this.showSellInputs = false;
            } else {
                this.showSellInputs = true;
            }

            // Is billback false?
            if (!this.form.id && !Boolean(Number(this.form.bill_back))) {
                // Is the service type is hotel and country the UK? If so set bill back to true
                this.form.service_type_id == 5 && this.form.country_id == 1 ? this.form.bill_back = 1 : this.form.bill_back = 0;
            }
        });


    }
}
</script>
